import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, keyframes, Theme } from '@mui/material/styles';
import React from 'react';

import { ColorProps, SizeProps } from '../../@types';
import { Switch } from '../../components/atoms/Switch';
import { REFRESH_INTERVAL_SECONDS } from '../../constants/service-activity.constant';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

/** Text and fills sitting on the dark band. Derived from the theme rather than
 *  written as rgba literals, but resolving to the same values ServiceListHeader
 *  uses so the two headers stay visually identical. */
const onDark = (opacity: number) => (theme: Theme) => alpha(theme.palette.common.white, opacity);
const WHITE = (theme: Theme) => theme.palette.common.white;

export interface HomeActivityHeaderProps {
  refreshEnabled: boolean;
  onRefreshToggle: () => void;
  lastUpdatedTime: string;
  /** Total open jobs across the whole payload — the one number worth a glance. */
  grandTotal: number;
  isFetching: boolean;
}

/**
 * Dark control-room band across the top of the page. Mirrors
 * `pages/Services/ServiceListHeader.tsx` so the two primary screens share a
 * silhouette.
 */
export const HomeActivityHeader: React.FC<HomeActivityHeaderProps> = ({
  refreshEnabled,
  onRefreshToggle,
  lastUpdatedTime,
  grandTotal,
  isFetching,
}) => (
  <Box
    component="header"
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      flexWrap: 'wrap',
      bgcolor: 'v4.sidebar',
      color: WHITE,
      px: '20px',
      py: '12px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box component="h1" sx={{ m: 0, fontSize: '14px', fontWeight: 700, color: WHITE }}>
        Service Activity
      </Box>
      {grandTotal > 0 && (
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 400,
            fontVariantNumeric: 'tabular-nums',
            color: onDark(0.85),
            bgcolor: onDark(0.12),
            px: 1,
            py: '2px',
            borderRadius: '10px',
          }}
        >
          {grandTotal.toLocaleString()} open jobs
        </Box>
      )}
    </Box>

    <Stack direction="row" alignItems="center" gap={1.5}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Box
          aria-hidden
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: refreshEnabled ? 'success.main' : 'v4.sidebarTextMuted',
            animation: refreshEnabled && isFetching ? `${pulse} 1.1s ease-in-out infinite` : 'none',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        {/* Only the live/paused state is announced. The timestamp is excluded
            deliberately: it changes every 120s on its own, and announcing it
            would interrupt a screen-reader user twice a minute for something
            they did not ask for. The matrix is never a live region either —
            that would read out hundreds of cells per refresh. */}
        <Box component="span" sx={{ fontSize: '11px', color: onDark(0.85) }} aria-live="polite">
          {refreshEnabled ? `Live · every ${REFRESH_INTERVAL_SECONDS}s` : 'Paused'}
        </Box>
        {lastUpdatedTime && (
          <Box component="span" sx={{ fontSize: '11px', color: onDark(0.85) }}>
            {`· updated ${lastUpdatedTime}`}
          </Box>
        )}
      </Stack>

      <Switch
        size={SizeProps.Small}
        color={ColorProps.Success}
        checked={refreshEnabled}
        onChange={onRefreshToggle}
        ariaLabel={`Auto refresh every ${REFRESH_INTERVAL_SECONDS} seconds`}
        testDataId="home-refresh-toggle"
      />
    </Stack>
  </Box>
);
